/**
 * Tool access by role — single source of truth for which roles can use each tool.
 * Mirrors backend/src/config/toolAccess.js — keep in sync.
 */
import {
  PencilSquareIcon,
  ArrowTrendingUpIcon,
  WrenchScrewdriverIcon,
  BoltIcon,
  MapPinIcon,
  ChartBarIcon,
  DocumentArrowUpIcon,
  PresentationChartLineIcon,
  CursorArrowRaysIcon,
  MegaphoneIcon,
  ClipboardDocumentCheckIcon,
  GlobeAltIcon,
  PaintBrushIcon,
  PhoneArrowUpRightIcon,
  PhotoIcon,
  Squares2X2Icon,
  LifebuoyIcon,
} from '@heroicons/react/24/outline';
import { ROLES, ROLE_LABELS } from './rolePermissions';
import MetaLogo from '../components/icons/MetaLogo';

export const TOOL_KEYS = {
  TECHNICAL_SEO_AUDIT: 'technical-seo-audit',
  KEYWORD_TRACKING: 'keyword-tracking',
  GBP_AUDIT: 'gbp-audit',
  SEO_INTELLIGENCE: 'seo-intelligence',
  // Daily Search Console monitoring — mirrors the backend key. The drift test
  // compares these lists, so a key added on one side only fails CI by design.
  GSC_MONITOR: 'gsc-monitor',
  WEBSITE_SCAN: 'website-scan',
  AD_APPROVALS: 'ad-approvals',
  CONTENT_STUDIO: 'content-studio',
  LEADS_TRACKER: 'leads-tracker',
  QUALITY_CONTROL: 'quality-control',
  CAMPAIGN_STUDIO: 'campaign-studio',
  CAMPAIGN_TRACKER: 'campaign-tracker',
  META_CREATIVES: 'meta-creatives',
  EDIT_STUDIO: 'edit-studio',
  AD_SAFE: 'ad-safe',
  ADS_AUDIT: 'ads-audit',
  MARKET_INTEL: 'market-intel',
  LEAD_IQ: 'lead-iq',
  GHL_PIPELINE: 'ghl-pipeline',
  DOC_VERIFICATION: 'doc-verification',
  DOC_TO_HTML: 'doc-to-html',
  GHL_ATTRIBUTION: 'ghl-attribution',
  GHL_WORKFLOWS: 'ghl-workflows',
  APP_INFORMATION: 'app-information',
  TOOL_CATALOG: 'tool-catalog',
  SUPPORT_CENTER: 'support-center',
  CLIENT_ONBOARDING: 'client-onboarding',
  PENDING_WORK: 'pending-work',
  WORKFLOWS: 'workflows',
  BLOG_LEGACY: 'blog-legacy',
  CONTENT_LEGACY: 'content-legacy',
  // Mirrors the backend — see that file for why this key had to exist.
  IMAGE_LAB: 'image-lab',
  CONTACT_CENTER_INTEL: 'contact-center-intel',
};

/** Shipped defaults — overridden at runtime when loaded from the API. */
export const DEFAULT_TOOL_ROLE_ACCESS = {
  [TOOL_KEYS.TECHNICAL_SEO_AUDIT]: [ROLES.ADMIN],
  [TOOL_KEYS.KEYWORD_TRACKING]: [ROLES.ADMIN],
  [TOOL_KEYS.GBP_AUDIT]: [ROLES.ADMIN],
  // Manager included: the SEO lead approves their own location's backlog, so the
  // role that owns the decision has to be able to open the screen.
  [TOOL_KEYS.SEO_INTELLIGENCE]: [ROLES.ADMIN, ROLES.MANAGER],
  [TOOL_KEYS.GSC_MONITOR]: [ROLES.ADMIN, ROLES.MANAGER],
  [TOOL_KEYS.WEBSITE_SCAN]: [ROLES.ADMIN, ROLES.MANAGER],
  // A team member builds the session; sending and linking are gated separately
  // on the backend route, so read/build access is deliberately wider.
  [TOOL_KEYS.AD_APPROVALS]: [ROLES.ADMIN, ROLES.MANAGER, ROLES.TEAM_MEMBER],
  [TOOL_KEYS.CONTENT_STUDIO]: [ROLES.ADMIN, ROLES.MANAGER, ROLES.TEAM_MEMBER],
  [TOOL_KEYS.LEADS_TRACKER]: [ROLES.ADMIN, ROLES.MANAGER],
  [TOOL_KEYS.QUALITY_CONTROL]: [ROLES.ADMIN, ROLES.MANAGER, ROLES.TEAM_MEMBER],
  [TOOL_KEYS.CAMPAIGN_STUDIO]: [ROLES.ADMIN, ROLES.MANAGER],
  [TOOL_KEYS.CAMPAIGN_TRACKER]: [ROLES.ADMIN, ROLES.MANAGER],
  [TOOL_KEYS.META_CREATIVES]: [ROLES.ADMIN, ROLES.MANAGER],
  [TOOL_KEYS.EDIT_STUDIO]: [ROLES.ADMIN, ROLES.MANAGER],
  [TOOL_KEYS.IMAGE_LAB]: [ROLES.ADMIN, ROLES.MANAGER],
  [TOOL_KEYS.CONTACT_CENTER_INTEL]: [ROLES.ADMIN, ROLES.MANAGER],
  [TOOL_KEYS.AD_SAFE]: [ROLES.ADMIN, ROLES.MANAGER],
  [TOOL_KEYS.ADS_AUDIT]: [ROLES.ADMIN, ROLES.MANAGER],
  [TOOL_KEYS.MARKET_INTEL]: [ROLES.ADMIN, ROLES.MANAGER],
  [TOOL_KEYS.LEAD_IQ]: [ROLES.ADMIN, ROLES.MANAGER],
  [TOOL_KEYS.GHL_PIPELINE]: [ROLES.ADMIN],
  [TOOL_KEYS.DOC_VERIFICATION]: [ROLES.ADMIN],
  [TOOL_KEYS.DOC_TO_HTML]: [ROLES.ADMIN, ROLES.MANAGER, ROLES.TEAM_MEMBER],
  [TOOL_KEYS.GHL_ATTRIBUTION]: [ROLES.ADMIN, ROLES.MANAGER],
  [TOOL_KEYS.GHL_WORKFLOWS]: [ROLES.ADMIN, ROLES.MANAGER],
  [TOOL_KEYS.APP_INFORMATION]: [ROLES.ADMIN],
  [TOOL_KEYS.TOOL_CATALOG]: [ROLES.ADMIN],
  // The AGENT console. Team leaders get it by default because they are the support
  // team leads; team members are added individually via a SupportAgent row. Raising a
  // ticket is a separate ungated surface at /support.
  [TOOL_KEYS.SUPPORT_CENTER]: [ROLES.ADMIN, ROLES.MANAGER],
  // Every department works its own onboarding checklist, so team members need
  // the page. What they can DO there is gated separately by MANAGE_ONBOARDING.
  [TOOL_KEYS.CLIENT_ONBOARDING]: [ROLES.ADMIN, ROLES.MANAGER, ROLES.TEAM_MEMBER],
  // Every department has prerequisites of its own to clear, so members need
  // the page; acting on a checkpoint is gated separately by permission.
  [TOOL_KEYS.PENDING_WORK]: [ROLES.ADMIN, ROLES.MANAGER, ROLES.TEAM_MEMBER],
  [TOOL_KEYS.WORKFLOWS]: [ROLES.ADMIN, ROLES.MANAGER, ROLES.TEAM_MEMBER],
  [TOOL_KEYS.BLOG_LEGACY]: [ROLES.ADMIN, ROLES.MANAGER, ROLES.TEAM_MEMBER],
  [TOOL_KEYS.CONTENT_LEGACY]: [ROLES.ADMIN, ROLES.MANAGER, ROLES.TEAM_MEMBER],
};

export const TOOL_DEFINITIONS = [
  { key: TOOL_KEYS.TECHNICAL_SEO_AUDIT, label: 'Technical SEO Audit', group: 'SEO Reports' },
  { key: TOOL_KEYS.KEYWORD_TRACKING, label: 'Keyword Tracking Report', group: 'SEO Reports' },
  { key: TOOL_KEYS.GBP_AUDIT, label: 'GBP Audit', group: 'SEO Reports' },
  { key: TOOL_KEYS.QUALITY_CONTROL, label: 'Quality Control', group: 'Quality Control' },
  // Retired — CODE_RETIRED_TOOL_KEYS (hidden from hub; no task creation on backend).
  { key: TOOL_KEYS.SEO_INTELLIGENCE, label: 'SEO Opportunity Centre (retired)', group: 'SEO Reports' },
  { key: TOOL_KEYS.GSC_MONITOR, label: 'Search Console Monitor (retired)', group: 'SEO Reports' },
  { key: TOOL_KEYS.WEBSITE_SCAN, label: 'Website Scan (retired)', group: 'SEO Reports' },
  { key: TOOL_KEYS.AD_APPROVALS, label: 'Ad Approvals', group: 'Paid Media' },
  { key: TOOL_KEYS.CONTENT_STUDIO, label: 'Content Studio', group: 'Content' },
  { key: TOOL_KEYS.LEADS_TRACKER, label: 'Leads Tracker', group: 'Paid Media' },
  { key: TOOL_KEYS.CONTACT_CENTER_INTEL, label: 'Call Centre Intelligence', group: 'Contact Centre' },
  { key: TOOL_KEYS.CAMPAIGN_STUDIO, label: 'Campaign Studio', group: 'Paid Media' },
  { key: TOOL_KEYS.CAMPAIGN_TRACKER, label: 'Campaign Tracker', group: 'Paid Media' },
  { key: TOOL_KEYS.META_CREATIVES, label: 'Ad Creatives', group: 'Paid Media' },
  { key: TOOL_KEYS.EDIT_STUDIO, label: 'Editing Hub', group: 'Paid Media' },
  // Retired — CODE_RETIRED_TOOL_KEYS.
  { key: TOOL_KEYS.IMAGE_LAB, label: 'Image Lab (retired)', group: 'Paid Media' },
  { key: TOOL_KEYS.AD_SAFE, label: 'Ad Safe', group: 'Paid Media' },
  { key: TOOL_KEYS.ADS_AUDIT, label: 'Ads Audit', group: 'Paid Media' },
  { key: TOOL_KEYS.MARKET_INTEL, label: 'Market Intel', group: 'Paid Media' },
  { key: TOOL_KEYS.LEAD_IQ, label: 'Lead IQ', group: 'Paid Media' },
  { key: TOOL_KEYS.GHL_PIPELINE, label: 'GHL Pipeline Builder', group: 'Automation' },
  { key: TOOL_KEYS.DOC_VERIFICATION, label: 'Document Verification', group: 'Automation' },
  { key: TOOL_KEYS.DOC_TO_HTML, label: 'Doc → HTML Converter', group: 'Content' },
  { key: TOOL_KEYS.GHL_ATTRIBUTION, label: 'GHL Attribution Center', group: 'Automation' },
  { key: TOOL_KEYS.GHL_WORKFLOWS, label: 'GHL Workflows', group: 'Automation' },
  { key: TOOL_KEYS.SUPPORT_CENTER, label: 'Support Center', group: 'Platform' },
  { key: TOOL_KEYS.CLIENT_ONBOARDING, label: 'Client Onboarding', group: 'Platform' },
  { key: TOOL_KEYS.PENDING_WORK, label: 'Pending Work', group: 'Platform' },
  { key: TOOL_KEYS.WORKFLOWS, label: 'Workflows', group: 'Platform' },
  { key: TOOL_KEYS.APP_INFORMATION, label: 'App Information (admin)', group: 'Platform', adminOnly: true },
  { key: TOOL_KEYS.TOOL_CATALOG, label: 'Tool Catalog (admin)', group: 'Platform', adminOnly: true },
];

export const CONFIGURABLE_ROLES = [
  { key: ROLES.MANAGER, label: ROLE_LABELS.manager },
  { key: ROLES.TEAM_MEMBER, label: ROLE_LABELS.team_member },
];

let runtimeAccess = null;

export function setRuntimeToolAccess(map) {
  runtimeAccess = map && typeof map === 'object' ? map : null;
}

export function getToolRoleAccess() {
  return runtimeAccess || DEFAULT_TOOL_ROLE_ACCESS;
}

/**
 * Tools the agency has switched off, delivered with the signed-in user.
 *
 * Deliberately a separate map from role access. "Which roles may open this" and
 * "do we run this at all" are different questions, and folding the second into
 * the first is what made "we don't use Ads Audit" unexpressible: admins pass the
 * role check unconditionally, so the tool stayed on their menu no matter what
 * was saved.
 *
 * Only switched-OFF keys arrive, so an unknown key is in use — a tool shipped
 * tomorrow appears rather than silently hiding.
 */
/**
 * Retired in code — keep in sync with backend `tool-enablement-service` CODE_RETIRED_TOOLS.
 * Hub hidden; routes blocked; GSC / Opportunity task creation disabled.
 */
export const CODE_RETIRED_TOOL_KEYS = new Set([
  TOOL_KEYS.SEO_INTELLIGENCE,
  TOOL_KEYS.GSC_MONITOR,
  TOOL_KEYS.WEBSITE_SCAN,
  TOOL_KEYS.IMAGE_LAB,
]);

let runtimeRetired = null;

export function setRuntimeToolsInUse(map) {
  if (!map || typeof map !== 'object') {
    runtimeRetired = null;
    return;
  }
  runtimeRetired = new Set(Object.entries(map).filter(([, on]) => on === false).map(([k]) => k));
}

export const isToolInUse = (toolKey) => {
  if (CODE_RETIRED_TOOL_KEYS.has(toolKey)) return false;
  return !runtimeRetired || !runtimeRetired.has(toolKey);
};

/** @deprecated use getToolRoleAccess — kept for compatibility */
export const TOOL_ROLE_ACCESS = DEFAULT_TOOL_ROLE_ACCESS;

/** DB catalog registry keys → tool key */
export const REGISTRY_KEY_TO_TOOL = {
  serpstat_technical_audit: TOOL_KEYS.TECHNICAL_SEO_AUDIT,
  serpstat_keyword_report: TOOL_KEYS.KEYWORD_TRACKING,
  gbp_audit: TOOL_KEYS.GBP_AUDIT,
  ghl_pipeline_builder: TOOL_KEYS.GHL_PIPELINE,
  content_studio: TOOL_KEYS.CONTENT_STUDIO,
  leads_tracker: TOOL_KEYS.LEADS_TRACKER,
  campaign_studio: TOOL_KEYS.CAMPAIGN_STUDIO,
  meta_creatives: TOOL_KEYS.META_CREATIVES,
  edit_studio: TOOL_KEYS.EDIT_STUDIO,
  ad_safe: TOOL_KEYS.AD_SAFE,
  ads_audit: TOOL_KEYS.ADS_AUDIT,
  market_intel: TOOL_KEYS.MARKET_INTEL,
  lead_iq: TOOL_KEYS.LEAD_IQ,
  ghl_attribution: TOOL_KEYS.GHL_ATTRIBUTION,
  doc_verification: TOOL_KEYS.DOC_VERIFICATION,
  support_center: TOOL_KEYS.SUPPORT_CENTER,
  gsc_monitor: TOOL_KEYS.GSC_MONITOR,
  website_scan: TOOL_KEYS.WEBSITE_SCAN,
};

export const canAccessTool = (role, toolKey) => {
  if (!role || !toolKey) return false;
  // Checked before the role, and before the admin shortcut: a retired tool is
  // closed to everyone, or "not in use" would mean nothing more than "hidden
  // from the menu of people who were not admins anyway".
  if (!isToolInUse(toolKey)) return false;
  if (role === ROLES.ADMIN) return true;
  const access = getToolRoleAccess();
  return (access[toolKey] || []).includes(role);
};

export const canAccessToolAsUser = (user, toolKey) =>
  canAccessTool(user?.role, toolKey);

export const getAccessibleToolKeys = (role) =>
  Object.values(TOOL_KEYS).filter((key) => canAccessTool(role, key));

export const getAccessibleHubTools = (role) =>
  TOOL_HUB_CARDS.filter((tool) => {
    if (tool.status !== 'live' || !tool.to) return false;
    if (tool.toolKey) return canAccessTool(role, tool.toolKey);
    return canAccessToolsHub(role);
  });

export function getToolMeta(toolKey) {
  const card = TOOL_HUB_CARDS.find((t) => (t.toolKey || t.key) === toolKey);
  if (card?.to) {
    return { toolKey: card.toolKey || card.key, name: card.name, path: card.to };
  }
  const def = TOOL_DEFINITIONS.find((t) => t.key === toolKey);
  if (def) {
    const linked = TOOL_HUB_CARDS.find((t) => t.toolKey === toolKey);
    return { toolKey, name: def.label, path: linked?.to || null };
  }
  return null;
}

export const canAccessToolsHub = (role) =>
  getAccessibleToolKeys(role).some((key) => key !== TOOL_KEYS.TOOL_CATALOG);

/** Hub card metadata — filtered by canAccessTool in ToolsHubPage */
export const TOOL_HUB_CARDS = [
  {
    key: TOOL_KEYS.SUPPORT_CENTER,
    toolKey: TOOL_KEYS.SUPPORT_CENTER,
    name: 'Support Center',
    to: '/support-center',
    status: 'live',
    icon: LifebuoyIcon,
    accent: 'brand',
    tag: 'Platform',
    description: 'Every DPM support ticket in one queue — Hi5, Concierge, DentalIQ, ShiftFlo, the portal and here. Triage, reply, track SLAs.',
  },
  {
    key: TOOL_KEYS.TECHNICAL_SEO_AUDIT,
    toolKey: TOOL_KEYS.TECHNICAL_SEO_AUDIT,
    name: 'Technical SEO Audit',
    to: '/tools/technical-seo-audit',
    status: 'live',
    icon: WrenchScrewdriverIcon,
    accent: 'brand',
    tag: 'Audit',
    description: 'Run a technical site audit for any location — crawl, analyze, and generate findings with live progress.',
  },
  {
    key: TOOL_KEYS.KEYWORD_TRACKING,
    toolKey: TOOL_KEYS.KEYWORD_TRACKING,
    name: 'Keyword Tracking Report',
    to: '/tools/keyword-tracking',
    status: 'live',
    icon: ArrowTrendingUpIcon,
    accent: 'primary',
    tag: 'SEO',
    description: 'Generate keyword opportunity reports with real-time status and a live event log.',
  },
  {
    key: TOOL_KEYS.GBP_AUDIT,
    toolKey: TOOL_KEYS.GBP_AUDIT,
    name: 'GBP Audit',
    to: '/tools/gbp-audit',
    status: 'live',
    icon: MapPinIcon,
    accent: 'success',
    tag: 'Local',
    description: 'Audit Google Business Profile listings and produce local SEO recommendations per location.',
  },
  /*
   * RETIRED — SEO Opportunity Centre, Website Scan, Search Console Monitor, Image Lab.
   * Cards removed from hub; backend CODE_RETIRED_TOOLS blocks the tool UI/routes.
   * Restore by removing keys from CODE_RETIRED_TOOL_KEYS (frontend) and CODE_RETIRED_TOOLS (backend).
   */
  {
    key: TOOL_KEYS.AD_APPROVALS,
    toolKey: TOOL_KEYS.AD_APPROVALS,
    name: 'Ad Approvals',
    to: '/tools/ad-approvals',
    status: 'live',
    icon: ClipboardDocumentCheckIcon,
    accent: 'warning',
    tag: 'Paid Media',
    description: 'Get client sign-off on ad copy, campaign structure and keywords before anything goes live — versioned, commented on, and linked to the campaign that ran.',
  },
  {
    key: TOOL_KEYS.CONTENT_STUDIO,
    toolKey: TOOL_KEYS.CONTENT_STUDIO,
    name: 'Content Studio',
    to: '/content',
    status: 'live',
    icon: PencilSquareIcon,
    accent: 'accent',
    tag: 'Content',
    description: 'End-to-end content workflow — location workbook, topic engine, article pipeline, fan-out, and handover.',
  },
  {
    key: TOOL_KEYS.LEADS_TRACKER,
    toolKey: TOOL_KEYS.LEADS_TRACKER,
    name: 'Leads Tracker',
    to: '/leads',
    status: 'live',
    icon: PresentationChartLineIcon,
    accent: 'primary',
    tag: 'Paid Media',
    description: 'Paid-media performance & routing — GHL lead quality, KPIs, budgets, escalations, and team workflows.',
  },
  {
    key: TOOL_KEYS.CONTACT_CENTER_INTEL,
    toolKey: TOOL_KEYS.CONTACT_CENTER_INTEL,
    name: 'Call Centre Intelligence',
    to: '/call-centre',
    status: 'live',
    icon: PhoneArrowUpRightIcon,
    accent: 'primary',
    tag: 'Contact Centre',
    description: 'Per-call facts from GoHighLevel — dials, connects, connect rate by office and agent, and leads dialled repeatedly without ever being reached.',
  },
  {
    key: TOOL_KEYS.CAMPAIGN_STUDIO,
    toolKey: TOOL_KEYS.CAMPAIGN_STUDIO,
    name: 'Campaign Studio',
    to: '/campaigns',
    status: 'live',
    icon: MegaphoneIcon,
    accent: 'warning',
    tag: 'Paid Media',
    description: 'AI campaign generator — brief → geo → keywords → playbook → Ads Editor CSVs for dental Google Ads launches.',
  },
  {
    key: TOOL_KEYS.CAMPAIGN_TRACKER,
    toolKey: TOOL_KEYS.CAMPAIGN_TRACKER,
    name: 'Campaign Tracker',
    to: '/tools/campaign-tracker',
    status: 'live',
    icon: ChartBarIcon,
    accent: 'primary',
    tag: 'Paid Media',
    description: 'Track live campaign performance per location — pick a location to open its tracker.',
  },
  {
    key: TOOL_KEYS.META_CREATIVES,
    toolKey: TOOL_KEYS.META_CREATIVES,
    name: 'Ad Creatives',
    to: '/meta-creatives',
    status: 'live',
    icon: MetaLogo,
    accent: 'meta',
    tag: 'Paid Media',
    description: 'Directed ad studio for Meta and Google — image ads + copy from your Market Intel report, act by act, ready to launch.',
  },
  {
    key: TOOL_KEYS.AD_SAFE,
    toolKey: TOOL_KEYS.AD_SAFE,
    name: 'Ad Safe',
    to: '/ad-safe',
    status: 'live',
    icon: PhotoIcon,
    accent: 'success',
    tag: 'Paid Media',
    description: 'Resize one image into official Meta or Google ad sizes — pick formats, generate in parallel, download each or as a zip.',
  },
  {
    key: TOOL_KEYS.EDIT_STUDIO,
    toolKey: TOOL_KEYS.EDIT_STUDIO,
    name: 'Editing Hub',
    to: '/edit-studio',
    status: 'live',
    icon: PaintBrushIcon,
    accent: 'purple',
    tag: 'Paid Media',
    description: 'Independent creative canvas — start blank or open from Ad Creatives, edit layers, export PNG.',
  },
  {
    key: TOOL_KEYS.ADS_AUDIT,
    toolKey: TOOL_KEYS.ADS_AUDIT,
    name: 'Ads Audit',
    to: '/ads-audit',
    status: 'live',
    icon: ClipboardDocumentCheckIcon,
    accent: 'accent',
    tag: 'Paid Media',
    description: 'Google Ads audit vs GHL lead truth — health score, waste findings, true CPL, and TaskOps to-dos.',
  },
  {
    key: TOOL_KEYS.MARKET_INTEL,
    toolKey: TOOL_KEYS.MARKET_INTEL,
    name: 'Market Intel',
    to: '/market-intel',
    status: 'live',
    icon: GlobeAltIcon,
    accent: 'brand',
    tag: 'Paid Media',
    description: 'Pre-campaign practice intelligence — demand, competition, demographics, and an AI decision on what to advertise, where, and how.',
  },
  {
    key: TOOL_KEYS.LEAD_IQ,
    toolKey: TOOL_KEYS.LEAD_IQ,
    name: 'Lead IQ',
    to: '/lead-iq',
    status: 'live',
    icon: ClipboardDocumentCheckIcon,
    accent: 'success',
    tag: 'Paid Media',
    description: 'Grade every GHL lead into call-priority bands and open a front-desk qualification workspace.',
  },
  {
    key: TOOL_KEYS.GHL_PIPELINE,
    toolKey: TOOL_KEYS.GHL_PIPELINE,
    name: 'GHL Pipeline Builder',
    to: '/tools/ghl',
    status: 'live',
    icon: BoltIcon,
    accent: 'warning',
    tag: 'Automation',
    description: 'Create GHL pipeline templates and run them across location sub-accounts with the Automator extension.',
  },
  {
    key: TOOL_KEYS.DOC_VERIFICATION,
    toolKey: TOOL_KEYS.DOC_VERIFICATION,
    name: 'Document Verification',
    to: '/tools/verify',
    status: 'live',
    icon: BoltIcon,
    accent: 'success',
    tag: 'Verification',
    description: "AI-scores a task's submitted deliverable(s) against the task; auto-passes at or above the threshold, else routes to manual review.",
  },
  {
    key: TOOL_KEYS.DOC_TO_HTML,
    toolKey: TOOL_KEYS.DOC_TO_HTML,
    name: 'Doc → HTML Converter',
    to: '/tools/doc-to-html',
    status: 'live',
    icon: DocumentArrowUpIcon,
    accent: 'accent',
    tag: 'Convert',
    description: 'Upload a Word .docx file and convert it to clean HTML — preview and download the result.',
  },
  {
    key: TOOL_KEYS.GHL_ATTRIBUTION,
    toolKey: TOOL_KEYS.GHL_ATTRIBUTION,
    name: 'GHL Attribution Center',
    to: '/tools/ghl-attribution',
    status: 'live',
    icon: CursorArrowRaysIcon,
    accent: 'primary',
    tag: 'Attribution',
    description: 'Manage a shared GHL custom-field library, push or remove fields across locations, and copy the LP script, Ads UTM template, and webhook map.',
  },
  {
    key: TOOL_KEYS.GHL_WORKFLOWS,
    toolKey: TOOL_KEYS.GHL_WORKFLOWS,
    name: 'GHL Workflows',
    to: '/tools/ghl-workflows',
    status: 'live',
    icon: BoltIcon,
    accent: 'warning',
    tag: 'Automation',
    description: 'See each active location\'s DPM-managed GHL workflows — Call Reports and the DentalIQ pipeline trackers — and fetch the latest status across every client.',
  },
  {
    key: TOOL_KEYS.APP_INFORMATION,
    toolKey: TOOL_KEYS.APP_INFORMATION,
    name: 'App Information',
    to: '/tools/app-information',
    status: 'live',
    icon: Squares2X2Icon,
    accent: 'purple',
    tag: 'Insights',
    category: '8. App Information',
    description: 'Browse DPM projects and open Spoonfeed, a conversational interface for project knowledge.',
  },
  {
    key: 'analytics',
    name: 'Analytics',
    status: 'soon',
    icon: ChartBarIcon,
    accent: 'brand',
    tag: 'Insights',
    description: 'Track rankings, traffic, and content performance across every location in one view.',
  },
  {
    key: 'rank',
    name: 'Rank Tracker',
    status: 'soon',
    icon: ArrowTrendingUpIcon,
    accent: 'primary',
    tag: 'SEO',
    description: 'Monitor keyword positions over time and catch movement before locations ask.',
  },
];

export const CURATED_REGISTRY_KEYS = new Set(Object.keys(REGISTRY_KEY_TO_TOOL));
